/**
 * What a weekly-point-summary row points at: a whole widget instance, or one
 * named part of one (docs/adr/0010).
 *
 * Encoded as a string so `WeeklyPointSummaryRow.widgetIds` keeps its shape. A
 * bare UUID means the whole widget — which is exactly what every row written
 * before parts existed contains, and what every sealed week snapshot still
 * holds — so no migration is needed and old rows keep resolving.
 */

const PART_SEPARATOR = "#";

export interface WidgetRef {
  widgetId: string;
  /** Absent for a whole-widget reference. */
  partKey?: string;
}

export function formatWidgetRef(widgetId: string, partKey?: string): string {
  return partKey === undefined
    ? widgetId
    : `${widgetId}${PART_SEPARATOR}${partKey}`;
}

export function parseWidgetRef(ref: string): WidgetRef {
  const index = ref.indexOf(PART_SEPARATOR);
  if (index === -1) return { widgetId: ref };
  return { widgetId: ref.slice(0, index), partKey: ref.slice(index + 1) };
}

/**
 * True when `ref` points at this widget at all — whole, or through any of its
 * parts. Deleting a widget must scrub every reference to it, not just the
 * bare-UUID one.
 */
export function refTargetsWidget(ref: string, widgetId: string): boolean {
  return parseWidgetRef(ref).widgetId === widgetId;
}
